import theme from '../../theme';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';

const READING_NOTES_CSS = `
  .notes-wrapper {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 20px 48px;
  }
  .notes-list {
    display: flex;
    flex-direction: column;
    gap: 36px;
    margin-top: 24px;
  }
  .note-card {
    background: ${theme.bgCard};
    border: 1px solid rgba(184, 149, 90, 0.25);
    border-radius: 4px;
    padding: 40px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    position: relative;
  }
  .note-card:hover {
    border-color: rgba(184, 149, 90, 0.55);
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
  }
  .note-header {
    margin-bottom: 24px;
    border-bottom: 1px solid rgba(184, 149, 90, 0.15);
    padding-bottom: 16px;
  }
  .note-thinker {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.18em;
    color: ${theme.gold};
    text-transform: uppercase;
    margin-bottom: 6px;
  }
  .note-book {
    font-family: 'Cormorant Garamond', serif;
    font-size: 26px;
    font-weight: 400;
    color: ${theme.textPrimary};
    margin: 0;
    font-style: italic;
  }
  .note-section {
    margin-bottom: 16px;
  }
  .note-section:last-of-type {
    margin-bottom: 0;
  }
  .section-label {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.14em;
    color: ${theme.gold};
    text-transform: uppercase;
    margin-bottom: 4px;
    display: block;
  }
  .section-content {
    font-family: 'EB Garamond', serif;
    font-size: 16px;
    color: ${theme.textMuted};
    line-height: 1.6;
    margin: 0;
  }
  .related-tool-block {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px dotted rgba(184, 149, 90, 0.2);
  }
  .tool-label {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.12em;
    color: #7a8a9a;
    text-transform: uppercase;
  }
  .tool-link {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${theme.gold};
    text-decoration: underline;
    text-underline-offset: 4px;
    transition: opacity 0.2s ease;
  }
  .tool-link:hover {
    opacity: 0.8;
  }
  @media (max-width: 600px) {
    .note-card {
      padding: 24px;
    }
    .note-book {
      font-size: 22px;
    }
  }
`;

const notesData = [
  {
    thinker: 'Ludwig von Mises',
    book: 'Human Action (1949)',
    sections: [
      {
        label: 'Core Idea',
        content: 'Praxeology: the formal, deductive study of human action. Mises argues that economics is not about math, but about purposeful human choice under conditions of scarcity. Humans act to replace a less satisfactory state with a more satisfactory one.'
      },
      {
        label: 'Why It Matters',
        content: 'It refutes mainstream mechanical models that treat economic cycles as equations in search of equilibrium. Understanding economics as human action reminds us that markets are dynamic and subjective, driven by human plans.'
      },
      {
        label: 'Real-World Relevance',
        content: 'Every plan - a business, a budget, a portfolio - is built on subjective human valuations, not static formulas. Volatility often just reflects plans being revised in real time. Mises’s framework casts doubt on any model that treats human choice as a predictable physical constant.'
      }
    ],
    toolText: 'Calculation Problem Simulator',
    toolPath: '/calculation-problem'
  },
  {
    thinker: 'Friedrich Hayek',
    book: 'Prices and Production (1931)',
    sections: [
      {
        label: 'Core Idea',
        content: 'The capital structure of production is heterogeneous and extended in time. When the central bank expands credit and artificially lowers interest rates, it fools capital builders into financing long-duration processes without a corresponding pool of real savings.'
      },
      {
        label: 'Why It Matters',
        content: 'It explains why credit booms must inevitably end in capital liquidations (busts). The boom represents structural malinvestment: resources are pulled into early-stage capital production that cannot be finished.'
      },
      {
        label: 'Real-World Relevance',
        content: 'This is why credit-cycle indicators matter for understanding where an economy actually stands: long-duration projects financed during a credit expansion may rest on distorted signals rather than genuine demand.'
      }
    ],
    toolText: 'Garrison Triangle Model',
    toolPath: '/garrison-triangle'
  },
  {
    thinker: 'Friedrich Hayek',
    book: 'The Use of Knowledge in Society (1945)',
    sections: [
      {
        label: 'Core Idea',
        content: 'Economic coordination relies on dispersing, subjective, and tacit knowledge held by individual actors. Prices act as an information communication network, translating complex changes in relative scarcity instantly into localized actions.'
      },
      {
        label: 'Why It Matters',
        content: 'Central planning bodies cannot coordinate resource allocation efficiently because they cannot centralize the necessary knowledge. Price signals are the only mechanism that can coordinate decentralized human plans.'
      },
      {
        label: 'Real-World Relevance',
        content: 'The framework suggests that any strategy - corporate, governmental, or personal - built primarily on centralized forecasts faces a structural information problem, especially where price discovery is distorted.'
      }
    ],
    toolText: 'Interest Rate Data',
    toolPath: '/fred'
  },
  {
    thinker: 'Ludwig Lachmann',
    book: 'Capital and Its Structure (1956)',
    sections: [
      {
        label: 'Core Idea',
        content: 'Capital is heterogeneous and structurally aligned. Individual capital assets do not simply add up; they must fit together like puzzle pieces into plans. When macro policies change, these plans fail, and capital must be restructured.'
      },
      {
        label: 'Why It Matters',
        content: 'It emphasizes that capital is a dynamic structure, not a homogeneous aggregate. If macro adjustments create clashes, physical equipment and resource investments must be restructured, which involves loss.'
      },
      {
        label: 'Real-World Relevance',
        content: 'Lachmann’s framework suggests that how capital assets fit together - not just their book-value totals - determines whether a plan actually works.'
      }
    ],
    toolText: 'Ask Austrian Assistant',
    toolPath: '/ask-austrian'
  }
];

function ReadingNotesPage() {
  useEffect(() => {
    document.title = "Reading Notes - Austrian Process";
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonicalLink ? canonicalLink.getAttribute('href') : '';
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://www.austrianprocess.com/reading-notes');
    }
    return () => {
      if (canonicalLink && previousCanonical) {
        canonicalLink.setAttribute('href', previousCanonical);
      }
    };
  }, []);

  return (
    <div className="notes-wrapper">
      <style>{READING_NOTES_CSS}</style>
      <PageHeader 
        title="Reading Notes" 
        description="Analyses of foundational economic texts, summarizing core ideas and why they still matter. Last reviewed: June 2026."
      />
      <div className="notes-list">
        {notesData.map((note, idx) => (
          <div key={idx} className="note-card">
            <div className="note-header">
              <div className="note-thinker">{note.thinker}</div>
              <h2 className="note-book">{note.book}</h2>
            </div>
            
            {note.sections.map((section, sIdx) => (
              <div key={sIdx} className="note-section">
                <span className="section-label">{section.label}</span>
                <p className="section-content">{section.content}</p>
              </div>
            ))}

            <div className="related-tool-block">
              <span className="tool-label">Related Tool:</span>
              <Link to={note.toolPath} className="tool-link">
                {note.relatedTool} {note.toolText} &rarr;
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ReadingNotesPage;
